import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { secopsApi } from '../../lib/secops-api';

interface IocHit {
  [key: string]: unknown;
}

interface IocResult {
  ioc_type: string;
  value: string;
  time_range_days: number;
  hits: IocHit[];
  total_hits: number;
  execution_time_ms: number;
  error?: string;
}

export default function IocSearchPage() {
  const navigate = useNavigate();
  const [iocType, setIocType] = useState('ip');
  const [value, setValue] = useState('');
  const [timeRangeDays, setTimeRangeDays] = useState('30');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<IocResult | null>(null);
  const [creating, setCreating] = useState(false);

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!value.trim()) return;
    setSearching(true);
    setResult(null);
    try {
      const res = await secopsApi.post<{ data: IocResult }>('/secops/forensics/search-ioc', {
        ioc_type: iocType,
        value: value.trim(),
        time_range_days: parseInt(timeRangeDays),
      });
      setResult(res.data);
    } catch (err) {
      setResult({ ioc_type: iocType, value, time_range_days: parseInt(timeRangeDays), hits: [], total_hits: 0, execution_time_ms: 0, error: err instanceof Error ? err.message : 'Search failed' });
    }
    setSearching(false);
  }

  async function createInvestigation() {
    if (!result || creating) return;
    setCreating(true);
    try {
      const res = await secopsApi.post<{ data: { id: string } }>('/secops/investigations', {
        name: `IOC Investigation: ${iocType}=${value}`,
        description: `Investigating ${iocType} indicator: ${value}. Found on ${result.total_hits} endpoint(s) in the last ${result.time_range_days} days.`,
        severity: result.total_hits > 5 ? 'high' : result.total_hits > 0 ? 'medium' : 'low',
      });
      navigate(`/secops/forensics/${res.data.id}`);
    } catch { /* ignore */ }
    setCreating(false);
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">IOC Search</h1>
        <p className="text-sm text-gray-500 mt-1">Search for Indicators of Compromise across all endpoints</p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">IOC Type</label>
            <select value={iocType} onChange={(e) => setIocType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="ip">IP Address</option>
              <option value="domain">Domain</option>
              <option value="hash">File Hash (SHA256/MD5/SHA1)</option>
              <option value="filename">Filename</option>
              <option value="registry_key">Registry Key</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
            <input type="text" value={value} onChange={(e) => setValue(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder={iocType === 'ip' ? '192.168.1.100' : iocType === 'domain' ? 'malicious-domain.com' : iocType === 'hash' ? 'abc123...' : iocType === 'filename' ? 'mimikatz.exe' : 'HKLM\\SOFTWARE\\...'} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Time Range (days)</label>
            <input type="number" value={timeRangeDays} onChange={(e) => setTimeRangeDays(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" min="1" max="90" />
          </div>
        </div>
        <button type="submit" disabled={searching} className="px-6 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-500 disabled:opacity-50">
          {searching ? 'Searching...' : 'Search All Endpoints'}
        </button>
      </form>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`text-2xl font-bold ${result.total_hits > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {result.total_hits}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{result.total_hits > 0 ? 'Endpoints affected' : 'No matches found'}</p>
                  <p className="text-xs text-gray-500">Query: {result.ioc_type} = {result.value} ({result.time_range_days} days) | {result.execution_time_ms}ms</p>
                </div>
              </div>
              {result.total_hits > 0 && (
                <button onClick={createInvestigation} disabled={creating} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-500 disabled:opacity-50">
                  {creating ? 'Creating...' : 'Create Investigation'}
                </button>
              )}
            </div>
          </div>

          {result.error ? (
            <div className="bg-white rounded-lg shadow p-6 text-center text-red-500">{result.error}</div>
          ) : result.hits.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {Object.keys(result.hits[0]).map((col) => (
                        <th key={col} className="px-4 py-3 text-left font-medium text-gray-500 whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {result.hits.map((hit, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        {Object.values(hit).map((val, j) => (
                          <td key={j} className="px-4 py-3 text-gray-700 whitespace-nowrap max-w-xs truncate">
                            {Array.isArray(val) ? (val as unknown[]).join(', ') : String(val ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
